import { sourceEntryOf, sourceKindOf } from "../fieldSource/sourceKinds";
import type { FieldSourceItem } from "../fieldSource/types";

/** Where a field's Open Source (or Open Intake) action goes. */
export type FieldSourceTarget =
  | { kind: "case_file"; nodeId: string; title: string; page: number | null }
  | { kind: "intake"; intakeId: string; submissionId: string | null };

const text = (value: unknown) => String(value ?? "").trim();

/**
 * The openable source behind a field: a case file (also an evidence id the
 * fill resolved to one) or the intake submission it answered. Null for kinds
 * with nothing to open (Case Insights, profile, pasted text, filing rules).
 */
export function fieldSourceTarget(
  item: FieldSourceItem
): FieldSourceTarget | null {
  const source = sourceEntryOf(item.provenance);
  if (!source) return null;
  const metadata = source.source_metadata ?? {};
  const kind = sourceKindOf(source);
  if (kind === "intake") {
    const intakeId =
      text(metadata.outgoing_intake_id) || text(source.source_id);
    if (!intakeId) return null;
    return {
      kind: "intake",
      intakeId,
      submissionId: text(metadata.outgoing_intake_submission_id) || null
    };
  }
  if (kind !== "document") return null;
  const isCaseFile =
    source.source_type === "case_file" ||
    metadata.resolved_as === "case_file" ||
    source.citation?.source_type === "case_file";
  const nodeId = text(source.source_id);
  if (!isCaseFile || !nodeId) return null;
  return {
    kind: "case_file",
    nodeId,
    title: text(source.source_label) || "Source",
    page:
      typeof source.citation?.page === "number" ? source.citation.page : null
  };
}
